/**
 * 微信环境检测工具
 * 统一管理微信浏览器环境检测和相关功能
 */

/**
 * 检测是否在微信浏览器中
 * @returns {boolean}
 */
export function isWechatBrowser() {
  const ua = navigator.userAgent.toLowerCase()
  return ua.includes('micromessenger')
}

/**
 * 检测是否在微信小程序中
 * @returns {boolean}
 */
export function isWechatMiniProgram() {
  const ua = navigator.userAgent.toLowerCase()
  return ua.includes('miniprogram')
}

/**
 * 检测是否在微信内置浏览器中（包括小程序webview）
 * @returns {boolean}
 */
export function isInWechat() {
  return isWechatBrowser() || isWechatMiniProgram()
}

/**
 * 获取微信环境类型
 * @returns {string} 'wechat' | 'miniprogram' | 'browser'
 */
export function getWechatEnvironment() {
  if (isWechatMiniProgram()) {
    return 'miniprogram'
  } else if (isWechatBrowser()) {
    return 'wechat'
  } else {
    return 'browser'
  }
}

/**
 * 检测是否支持微信支付
 * @returns {boolean}
 */
export function supportWechatPay() {
  return isWechatBrowser() && typeof window.WeixinJSBridge !== 'undefined'
}

/**
 * 等待WeixinJSBridge准备就绪
 * @returns {Promise<boolean>}
 */
export function waitForWeixinJSBridge() {
  return new Promise((resolve) => {
    if (typeof window.WeixinJSBridge !== 'undefined') {
      resolve(true)
    } else {
      document.addEventListener('WeixinJSBridgeReady', () => {
        resolve(true)
      }, false)
      
      // 设置超时，避免无限等待
      setTimeout(() => {
        resolve(false)
      }, 5000)
    }
  })
}

/**
 * 设置微信环境标识
 * 为HTML元素添加data-wechat属性，便于CSS样式控制
 */
export function setWechatEnvironmentFlag() {
  const environment = getWechatEnvironment()
  
  if (environment !== 'browser') {
    document.documentElement.setAttribute('data-wechat', 'true')
    document.documentElement.setAttribute('data-wechat-env', environment)
  } else {
    document.documentElement.removeAttribute('data-wechat')
    document.documentElement.removeAttribute('data-wechat-env')
  }
}

/**
 * 隐藏微信浏览器的底部导航栏
 */
export function hideWechatNavigationBar() {
  if (!isWechatBrowser()) {
    return
  }
  
  // 设置页面高度为视口高度，防止底部导航栏显示
  const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
  }
  
  // 初始设置
  setViewportHeight()
  
  // 监听窗口大小变化
  window.addEventListener('resize', setViewportHeight)
  window.addEventListener('orientationchange', setViewportHeight)
}

/**
 * 初始化微信环境
 * 统一处理微信环境的初始化工作
 */
export function initWechatEnvironment() {
  // 设置环境标识
  setWechatEnvironmentFlag()
  
  // 如果在微信浏览器中，隐藏底部导航栏
  if (isWechatBrowser()) {
    hideWechatNavigationBar()
  }
  
  // 返回环境信息
  return {
    isWechat: isInWechat(),
    environment: getWechatEnvironment(),
    supportPay: supportWechatPay()
  }
}

/**
 * 微信环境检测结果
 * @typedef {Object} WechatEnvironmentInfo
 * @property {boolean} isWechat - 是否在微信环境中
 * @property {string} environment - 环境类型
 * @property {boolean} supportPay - 是否支持微信支付
 */

/**
 * 检测是否为公众号首次进入
 * 通过检查referrer和sessionStorage来判断
 * @returns {boolean}
 */
export function isWechatOfficialAccountFirstEntry() {
  // 必须在微信浏览器环境中
  if (!isWechatBrowser()) {
    return false
  }
  
  // 检查是否已经有访问记录
  const hasVisited = sessionStorage.getItem('wechat_visited')
  if (hasVisited) {
    return false
  }
  
  // 检查referrer是否来自微信公众号
  const referrer = document.referrer
  const isFromWechatOfficialAccount = 
    !referrer || // 直接打开（通常是从公众号菜单或链接）
    referrer.includes('mp.weixin.qq.com') || // 微信公众号文章页面
    referrer.includes('weixin.qq.com') // 其他微信相关页面
  
  // 如果是首次访问且来自公众号，标记已访问
  if (isFromWechatOfficialAccount) {
    sessionStorage.setItem('wechat_visited', 'true')
    console.log('🔍 检测到公众号首次进入，已标记访问状态')
    return true
  }
  
  return false
}

/**
 * 重置访问状态（用于测试或特殊情况）
 */
export function resetWechatVisitStatus() {
  sessionStorage.removeItem('wechat_visited')
  console.log('🔄 已重置微信访问状态')
}

/**
 * 获取完整的微信环境信息
 * @returns {WechatEnvironmentInfo}
 */
export function getWechatEnvironmentInfo() {
  return {
    isWechat: isInWechat(),
    isWechatBrowser: isWechatBrowser(),
    isWechatMiniProgram: isWechatMiniProgram(),
    environment: getWechatEnvironment(),
    supportPay: supportWechatPay(),
    userAgent: navigator.userAgent,
    isOfficialAccountFirstEntry: isWechatOfficialAccountFirstEntry()
  }
}

// 默认导出
export default {
  isWechatBrowser,
  isWechatMiniProgram,
  isInWechat,
  getWechatEnvironment,
  supportWechatPay,
  waitForWeixinJSBridge,
  setWechatEnvironmentFlag,
  hideWechatNavigationBar,
  initWechatEnvironment,
  getWechatEnvironmentInfo,
  isWechatOfficialAccountFirstEntry,
  resetWechatVisitStatus
}